import React, { useState } from "react";
import {
    Box,
    Badge,
    Popover,
    Stack,
    Typography,
    TextField,
    Chip,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
    Autocomplete,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { intl, DmsButton, DmsDateRangePicker, downloadUrl, getToken } from "dmsbasereact";
import moment from "moment";
import { getBaseURL } from "../../../build_config";
import { ColumnManager } from "./ColumnManager";

/**
 * ReportFilters - Component for filtering reports with a popover interface
 *
 * @param {Object} props
 * @param {Array} props.filterFields - Array of filter field definitions
 *   Each field: { name: string, label: string, type: 'text' | 'select' | 'multiselect', options?: Array<{value, label}> }
 *   - text: Free text input
 *   - select: Single selection dropdown with chips
 *   - multiselect: Multiple selection with chips (BI-style)
 * @param {Object} props.filters - Current filter values { fieldName: value | value[] }
 * @param {Function} props.onFiltersChange - Callback when filters change
 * @param {Function} props.onApply - Callback when Apply button is clicked
 * @param {Function} props.onClear - Callback when Clear button is clicked
 * @param {Function} props.onRefresh - Callback when Refresh button is clicked
 * @param {string} props.exportUrl - URL for exporting the report data
 * @param {Array} props.columns - Array of column definitions for column manager
 * @param {Object} props.columnVisibility - Current column visibility state
 * @param {Function} props.onColumnVisibilityChange - Callback when column visibility changes
 */
export const ReportFilters = ({
    filterFields = [],
    filters = {},
    onFiltersChange,
    onApply,
    onClear,
    onRefresh,
    exportUrl,
    columns = [],
    columnVisibility = {},
    onColumnVisibilityChange,
    showPdf = false,
    onAsyncExport = null,
    dateFilterLabel = null,
    showDateRange = true,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const token = getToken();

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleFieldChange = (name, value) => {
        onFiltersChange({
            ...filters,
            [name]: value,
        });
    };

    const handleApply = () => {
        onApply();
        handleClose();
    };

    const handleClear = () => {
        onClear();
        handleClose();
    };

    const handleExportClick = (event) => {
        setExportAnchorEl(event.currentTarget);
    };

    const handleExportClose = () => {
        setExportAnchorEl(null);
    };

    const handleExport = async (format) => {
        if (!exportUrl) return;

        // Build filters array for backend
        const filtersArray = [];
        Object.keys(filters).forEach((key) => {
            const value = filters[key];
            if (value !== "" && value !== null && value !== undefined) {
                if (Array.isArray(value) && value.length > 0) {
                    filtersArray.push({ name: key, value: value.join(",") });
                } else if (!Array.isArray(value)) {
                    filtersArray.push({ name: key, value: String(value) });
                }
            }
        });

        // Build hidden columns list from visibility state
        const hiddenColumns = [];
        if (columns.length > 0 && columnVisibility) {
            columns.forEach((col) => {
                const colKey = col.column_name || col.field;
                if (columnVisibility[colKey] === false) {
                    hiddenColumns.push(colKey);
                }
            });
        }

        if (onAsyncExport) {
            // All formats go through the async service when the caller supports it
            handleExportClose();
            onAsyncExport(format, filtersArray, hiddenColumns);
            return;
        }

        // Legacy sync path (pages that haven't opted in to async yet)
        const separator = exportUrl.includes("?") ? "&" : "?";
        const filterParam = filtersArray.length > 0
            ? `&filter=${encodeURIComponent(JSON.stringify(filtersArray))}`
            : "";
        const hiddenParam = hiddenColumns.length > 0
            ? `&hidden_columns=${encodeURIComponent(JSON.stringify(hiddenColumns))}`
            : "";
        const endpoint = `${exportUrl}${separator}format=${format}${filterParam}${hiddenParam}`;

        if (format === "pdf") {
            const fullUrl = `${getBaseURL()}${endpoint}`;
            const headers = token?.access_token
                ? { Authorization: `Bearer ${token.access_token}` }
                : {};
            try {
                const response = await fetch(fullUrl, { headers });
                if (response.ok) {
                    const blob = await response.blob();
                    const blobUrl = window.URL.createObjectURL(blob);
                    window.open(blobUrl, "_blank");
                }
            } catch (error) {
                console.error("Error opening PDF:", error);
            }
        } else {
            downloadUrl(endpoint, token);
        }
        handleExportClose();
    };

    // Count active filters
    const getActiveFilterCount = () => {
        let count = 0;
        Object.keys(filters).forEach((key) => {
            const value = filters[key];
            // Skip empty values
            if (value === "" || value === null || value === undefined) return;
            if (Array.isArray(value) && value.length === 0) return;

            // Date range counts as 1 filter
            if (key === "start_date" || key === "end_date") {
                if (key === "start_date" && filters.start_date) count++;
            } else {
                count++;
            }
        });
        return count;
    };

    const activeCount = getActiveFilterCount();
    const isOpen = Boolean(anchorEl);
    const isExportOpen = Boolean(exportAnchorEl);

    const iconStyle = { color: "white" };

    return (
        <Box>
            <Stack direction="row" spacing={0.5} alignItems="center">
                {/* Column manager */}
                {columns.length > 0 && onColumnVisibilityChange && (
                    <ColumnManager
                        columns={columns}
                        visibility={columnVisibility}
                        onVisibilityChange={onColumnVisibilityChange}
                    />
                )}

                {/* Export button */}
                {exportUrl && (
                    <>
                        <Tooltip title={intl.get("Export").d("Export")}>
                            <IconButton onClick={handleExportClick} size="small" sx={iconStyle}>
                                <DownloadIcon />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            anchorEl={exportAnchorEl}
                            open={isExportOpen}
                            onClose={handleExportClose}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                        >
                            <MenuItem onClick={() => handleExport("xlsx")}>Excel</MenuItem>
                            <MenuItem onClick={() => handleExport("csv")}>CSV</MenuItem>
                            {showPdf && <MenuItem onClick={() => handleExport("pdf")}>PDF</MenuItem>}
                        </Menu>
                    </>
                )}

                {/* Refresh button - also resets sorting */}
                {onRefresh && (
                    <Tooltip title={intl.get("RefreshAndResetSort").d("Refresh and Reset Sort")}>
                        <IconButton onClick={onRefresh} size="small" sx={iconStyle}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                )}

                {/* Clear filters button - only show if there are active filters */}
                {activeCount > 0 && (
                    <Tooltip title={intl.get("ClearFilters").d("Clear Filters")}>
                        <IconButton onClick={onClear} size="small" sx={iconStyle}>
                            <FilterAltOffIcon />
                        </IconButton>
                    </Tooltip>
                )}

                {/* Filter button with badge */}
                <Tooltip title={intl.get("Filters").d("Filters")}>
                    <IconButton onClick={handleOpen} size="small" sx={iconStyle}>
                        <Badge
                            badgeContent={activeCount}
                            color="error"
                            invisible={activeCount === 0}
                        >
                            <FilterListIcon />
                        </Badge>
                    </IconButton>
                </Tooltip>
            </Stack>

            {/* Filter popover */}
            <Popover
                open={isOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                PaperProps={{
                    sx: {
                        width: 320,
                        p: 2,
                        borderRadius: 2,
                    },
                }}
            >
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                    {intl.get("Filters").d("Filters")}
                </Typography>

                <Stack spacing={2}>
                    {/* Date range picker - first if enabled */}
                    {showDateRange && (
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                                {dateFilterLabel || intl.get("Period").d("Period")}
                            </Typography>
                            <DmsDateRangePicker
                                startValue={filters.start_date ? moment(filters.start_date) : null}
                                endValue={filters.end_date ? moment(filters.end_date) : null}
                                onChange={({ startDate, endDate }) => {
                                    onFiltersChange({
                                        ...filters,
                                        start_date: startDate && startDate.isValid() ? startDate.toISOString().split("T")[0] : null,
                                        end_date: endDate && endDate.isValid() ? endDate.toISOString().split("T")[0] : null,
                                    });
                                }}
                            />
                        </Box>
                    )}

                    {/* Other filter fields */}
                    {filterFields
                        .filter((field) => field.type !== "date_range")
                        .map((field) => {
                            if ((field.type === "select" || field.type === "multiselect") && field.options) {
                                const isMulti = field.type === "multiselect";
                                const currentValue = isMulti
                                    ? (filters[field.name] || []).map((v) => field.options.find((o) => o.value === v)).filter(Boolean)
                                    : field.options.find((o) => o.value === filters[field.name]) || null;

                                return (
                                    <Autocomplete
                                        key={field.name}
                                        multiple={isMulti}
                                        options={field.options}
                                        getOptionLabel={(option) => option.label}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        value={currentValue}
                                        onChange={(_, newValue) => {
                                            if (isMulti) {
                                                handleFieldChange(field.name, newValue.map((v) => v.value));
                                            } else {
                                                handleFieldChange(field.name, newValue ? newValue.value : "");
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label={field.label} size="small" />
                                        )}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <Chip
                                                    {...getTagProps({ index })}
                                                    key={option.value}
                                                    label={option.label}
                                                    size="small"
                                                />
                                            ))
                                        }
                                        size="small"
                                        disableCloseOnSelect={isMulti}
                                        limitTags={2}
                                    />
                                );
                            }
                            return (
                                <TextField
                                    key={field.name}
                                    label={field.label}
                                    value={filters[field.name] || ""}
                                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                    fullWidth
                                    size="small"
                                />
                            );
                        })}

                    {/* Action buttons */}
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <DmsButton variant="primary" onClick={handleApply} fullWidth>
                            {intl.get("Apply").d("Apply")}
                        </DmsButton>
                        <DmsButton variant="secondary" onClick={handleClear} fullWidth>
                            {intl.get("Clear").d("Clear")}
                        </DmsButton>
                    </Stack>
                </Stack>
            </Popover>
        </Box>
    );
};
